import React, { useMemo, useState } from "react";
import { PhoneReminderButton } from "./PhoneReminderButton";
import { usePhone } from "./usePhone";
import { useBrowserCallingSupport } from "./hooks/useBrowserCallingSupport";
import {
  formatPhoneNumberForDisplay,
  isAllowedPhoneNumber,
  normalizePhoneNumber,
} from "./phoneNumberUtils";
import { getPhoneCallStatusText } from "./utils/phoneStatusUtils";

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "64px",
  },
  number: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: 1.2,
  },
  actions: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "68px",
    minHeight: "34px",
    padding: "7px 14px",
    border: 0,
    borderRadius: "8px",
    color: "#ffffff",
    fontSize: "0.9rem",
    fontWeight: 700,
    cursor: "pointer",
    visibility: "visible",
  },
  callButton: {
    backgroundColor: "#1976d2",
  },
  hangupButton: {
    backgroundColor: "#d32f2f",
  },
  disabled: {
    cursor: "not-allowed",
    opacity: 0.65,
  },
  status: {
    color: "#a1acb7",
    fontSize: "0.78rem",
    fontWeight: 600,
    lineHeight: 1.2,
    maxWidth: "240px",
    overflowWrap: "anywhere",
  },
};

export const PhoneCallButton = ({ phoneNumber, parent, student }) => {
  const { status, error, isReady, isCalling, activeDestination, call, hangUp } =
    usePhone();
  const browserCallingSupport = useBrowserCallingSupport();
  const [localError, setLocalError] = useState(null);
  const [working, setWorking] = useState(false);

  const normalizedNumber = useMemo(
    () => normalizePhoneNumber(phoneNumber),
    [phoneNumber]
  );
  const displayNumber = useMemo(
    () => formatPhoneNumberForDisplay(phoneNumber),
    [phoneNumber]
  );
  const validNumber = isAllowedPhoneNumber(normalizedNumber);
  const isCurrentCall = activeDestination === normalizedNumber;
  const anotherCallActive = isCalling && activeDestination && !isCurrentCall;

  const disabled =
    working ||
    !validNumber ||
    !browserCallingSupport.supported ||
    (!isReady && !isCurrentCall) ||
    anotherCallActive;

  const getStatusText = (localOverride = localError) =>
    getPhoneCallStatusText({
      phoneNumber,
      validNumber,
      anotherCallActive,
      localError: localOverride,
      providerError: error,
      isCurrentCall,
      status,
      isReady,
      browserCallingSupport,
    });

  const handleCall = async () => {
    setLocalError(null);

    if (disabled) {
      setLocalError(getStatusText(null));
      return;
    }

    setWorking(true);

    try {
      await call(normalizedNumber, {
        parentName: parent?.fullname || null,
        studentId: student?.contactid || null,
        studentName: student?.fullname || null,
      });
    } catch (callError) {
      setLocalError(callError.message || "Call failed");
    } finally {
      setWorking(false);
    }
  };

  const handleHangUp = async () => {
    setLocalError(null);
    setWorking(true);

    try {
      await hangUp();
    } catch (hangupError) {
      setLocalError(hangupError.message || "Hang up failed");
    } finally {
      setWorking(false);
    }
  };

  const statusText = getStatusText();

  return (
    <div style={styles.wrapper} data-testid="phone-call-button">
      <div style={styles.number}>{displayNumber}</div>
      <div style={styles.actions}>
        {isCurrentCall ? (
          <button
            type="button"
            style={{
              ...styles.button,
              ...styles.hangupButton,
              ...(working ? styles.disabled : null),
            }}
            disabled={working}
            onClick={handleHangUp}
          >
            {working ? "Ending..." : "Hang Up"}
          </button>
        ) : (
          <button
            type="button"
            style={{
              ...styles.button,
              ...styles.callButton,
              ...(disabled ? styles.disabled : null),
            }}
            aria-disabled={disabled}
            onClick={handleCall}
          >
            {working ? "Calling..." : "Call"}
          </button>
        )}
        <PhoneReminderButton
          phoneNumber={phoneNumber}
          parent={parent}
          student={student}
        />
      </div>
      <span style={styles.status}>{statusText}</span>
    </div>
  );
};
